import type { createBrowserRouter } from 'react-router';

type AppRouter = ReturnType<typeof createBrowserRouter>;

// The app has no router basename, so its start page is the origin's root.
const HOME_PATH = '/';

// The DOKA logo in the portal header is a <button class="headerbar-logo"> (seen in the DevTools),
// not a link - its click handler does the page load from script, so no `href` gives it away.
const HEADER_LOGO_SELECTOR = '.headerbar-logo';

// The parts of the browser's Navigation API this file uses. Declared here rather than taken from
// lib.dom, which does not ship it in every TypeScript version the project may be built with.
interface NavigateEventLike {
    canIntercept: boolean;
    hashChange: boolean;
    downloadRequest: string | null;
    formData: FormData | null;
    navigationType: 'push' | 'replace' | 'reload' | 'traverse';
    destination: { url: string; sameDocument: boolean };
    intercept: (options: { handler: () => Promise<unknown>; focusReset?: 'manual'; scroll?: 'manual' }) => void;
}
interface NavigationLike {
    addEventListener: (type: 'navigate', listener: (event: NavigateEventLike) => void) => void;
    removeEventListener: (type: 'navigate', listener: (event: NavigateEventLike) => void) => void;
}

/** Whether a URL is the app's own start page (same origin, path "/", any query or hash). */
const isHomeUrl = (url: URL): boolean => url.origin === window.location.origin && url.pathname === HOME_PATH;

/**
 * Makes "go to the start page" a client-side navigation instead of a page load.
 *
 * The yellow DOKA header with its logo is rendered by the private @uag/react-core BaseLayout, and
 * a click on the logo loads the whole page again. That throws away everything the app had in
 * memory - above all the catalog index (tens of MB, several seconds to fetch, too big for
 * localStorage; see queryPersistence.ts) - and re-runs the portal chrome, cookie banner included.
 * Going to "/" through the router instead keeps the QueryClient, so the start page opens at once
 * on the data that is already there. The app's own title in the header (HeaderBrand) does the same
 * with `navigate('/')`.
 *
 * This code does not own that markup and cannot see how the logo navigates - the logo is a button
 * whose handler loads the page from script (`location.reload()`, an assignment to `location.href`,
 * ...) - so it hooks in at three levels:
 *
 * 1. A click on the header logo itself (HEADER_LOGO_SELECTOR) goes to the start page through the
 *    router, and the layout's own handler never runs. This is what catches `location.reload()`,
 *    which no navigation hook may touch (F5 has to keep reloading).
 * 2. The browser's Navigation API (Chrome, Edge): every navigation to "/" that would LEAVE the
 *    document is intercepted, whatever started it - the safety net for a logo whose markup changes.
 *    The router's own navigations are same-document ones and are left alone, so it cannot catch
 *    itself.
 * 3. A click listener for plain links to "/", found through the event's composed path so links
 *    inside shadow roots count too - for the browsers without that API.
 *
 * Everything a link may legitimately do stays untouched: clicks with a modifier key (open in a new
 * tab), other mouse buttons, `target="_blank"`, downloads, form submissions, other origins and
 * every other path.
 *
 * @returns a function that removes both hooks again
 */
export const installClientSideHomeLink = (router: AppRouter): (() => void) => {
    // `replace` only after the Navigation API intercepted: the browser has already added the history
    // entry itself then, and the router must adopt it rather than add a second one. A click handled
    // here, on the other hand, is a link click like any other and gets its own entry (back works).
    const goHome = (url: URL, replace: boolean) => router.navigate(`${url.pathname}${url.search}${url.hash}`, { replace });

    const handleNavigate = (event: NavigateEventLike) => {
        if (!event.canIntercept || event.hashChange || event.downloadRequest !== null || event.formData !== null) {
            return;
        }
        // 'reload' is F5, 'traverse' is back/forward: both must do what the browser does.
        if (event.navigationType !== 'push' && event.navigationType !== 'replace') {
            return;
        }
        // The router's own pushState navigations are same-document - never intercept those.
        if (event.destination.sameDocument) {
            return;
        }
        const url = new URL(event.destination.url);
        if (!isHomeUrl(url)) {
            return;
        }
        event.intercept({ focusReset: 'manual', scroll: 'manual', handler: () => goHome(url, true) });
    };

    const handleClick = (event: MouseEvent) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }
        const path = event.composedPath();
        if (path.some((node) => node instanceof Element && node.matches(HEADER_LOGO_SELECTOR))) {
            event.preventDefault();
            event.stopPropagation();
            void goHome(new URL(HOME_PATH, window.location.origin), false);
            return;
        }
        const anchor = path.find((node): node is HTMLAnchorElement => node instanceof HTMLAnchorElement);
        if (!anchor?.href || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) {
            return;
        }
        const url = new URL(anchor.href, window.location.href);
        if (!isHomeUrl(url)) {
            return;
        }
        // Stopped here as well: the layout's own click handler must not also run its full navigation.
        event.preventDefault();
        event.stopPropagation();
        void goHome(url, false);
    };

    const navigation = (window as unknown as { navigation?: NavigationLike }).navigation;
    navigation?.addEventListener('navigate', handleNavigate);
    document.addEventListener('click', handleClick, true);
    return () => {
        navigation?.removeEventListener('navigate', handleNavigate);
        document.removeEventListener('click', handleClick, true);
    };
};
